import React from "react";
var __jsx = React.createElement;
// Warning! Order is important
import "./styles/variables.css";
import "./styles/reset.css";
import "./styles/helpers.css";
import "./styles/globals.css";
import "./styles/common.css";
import "./styles/AB_TEST.css";
import "./styles/TEMP.css";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Suspense, useEffect } from "react";
import { oldUserIdPropertyEvent } from "./utils/dataLayerEvents";
import { unbxdAnalytics } from "./redux/actions/unbxdActions";
import { SELECTED_ENV, serverUrl, SHOW_DEBUG_INFO, TAMARA_WIDGET_ENVIRONMENT, TAMRA_WIDGET_PUBLIC_KEY } from "@globalConfig";
import useSentry from "./common/hooks/useSentry";
import { page404 as Page404 } from "./routes/asyncPages";
import { useFirstRenderHistoryLogic } from "@hooks/useFirstRender";
import useHashToLocalStoreEvent from "@hooks/useHashToLocalStoreEvent";
import { useSetReferral } from "@hooks/useGetReferral";
import { Toaster } from "react-hot-toast";
import ConfirmPopup from "@componentsShared/ConfirmPopup/ConfirmPopup";
import useScrollToTop from "@hooks/useScrollToTop";
import useInitDataLayerConfig from "@hooks/useInitDataLayerMeta";
import useIsWebView from "@hooks/useIsWebview";
import useIsIOS from "@hooks/useIsIOS";
import { useAuthActions, useSession } from "@/_MiniApps/auth/AuthContext";
import { SIGN_UP_URL } from "@/_MiniApps/auth/constants";
import useHelpers from "@hooks/useHelpers";
import useKeyboardDetector from "@hooks/useKeyboardDetector";
import { useMaintainSearchResultsDataStorage } from "@/pages/search/searchHooks/usePreserveData";
import { VideoRenderer } from "./_MiniApps/VideoRenderer/VideoRenderer";
import SonnerToast from "@/components/SonnerToast/SonnerToast";
import { ToastContainer } from "@/common/events/Toast";
import loadable from "@loadable/component";
import Loader from "@componentsShared/Loader/Loader";
import { GlobalPromoManager } from "@/_MiniApps/customized_promo_codes/components";
import useWebview from "@hooks/useWebview";
import useDisableClickWebview from "@hooks/useDisableClickWebview";
import { checkFeatureFlag } from "@/common/config/websiteConfig";
import { ConsentModal } from "@/components/ConsentModal/ConsentModal";
import { useConsent } from "@/common/Contexts/Consent/ConsentContext";
import { TamaraProvider } from "@hooks/useTamaraWidget";
import { useLang } from "@/common/Contexts/LanguageContext";
import useSupportGooglePay from "@hooks/useSupportGooglePay";
import useHideGTM from "@hooks/useHideGTM";

// Lazy load SyarahLabs using loadable/component
var fallback = {
  fallback: null
};
var SyarahLabs = loadable({
  resolved: {},
  chunkName: function chunkName() {
    return "_MiniApps-SyarahLabs-SyarahLabs";
  },
  isReady: function isReady(props) {
    var key = this.resolve(props);
    if (this.resolved[key] !== true) {
      return false;
    }
    if (typeof __webpack_modules__ !== 'undefined') {
      return !!__webpack_modules__[key];
    }
    return false;
  },
  importAsync: function importAsync() {
    return import(/* webpackChunkName: "_MiniApps-SyarahLabs-SyarahLabs" */"@/_MiniApps/SyarahLabs/SyarahLabs");
  },
  requireAsync: function requireAsync(props) {
    var _this = this;
    var key = this.resolve(props);
    this.resolved[key] = false;
    return this.importAsync(props).then(function (resolved) {
      _this.resolved[key] = true;
      return resolved;
    });
  },
  requireSync: function requireSync(props) {
    var id = this.resolve(props);
    if (typeof __webpack_require__ !== 'undefined') {
      return __webpack_require__(id);
    }
    return eval('module.require')(id);
  },
  resolve: function resolve() {
    if (require.resolveWeak) {
      return require.resolveWeak("@/_MiniApps/SyarahLabs/SyarahLabs");
    }
    return eval('require.resolve')("@/_MiniApps/SyarahLabs/SyarahLabs");
  }
}, fallback);
if (__IS_CSR__ && __IS_PROD_ENV__ || SELECTED_ENV === "preprod") {
  console.log = function no_console() {};
}
var RouteWithProviders = function RouteWithProviders(_ref) {
  var route = _ref.route,
    isLoggedIn = _ref.isLoggedIn;
  var _useHelpers = useHelpers(),
    createUrl = _useHelpers.createUrl,
    getCurrentUrl = _useHelpers.getCurrentUrl;
  var Element = route.component;
  var isWebView = useIsWebView();
  var webview = useWebview();
  var supportGooglePay = useSupportGooglePay();
  var hideGTM = useHideGTM(null);

  // only Allow certain pages to be opened in webview
  // defined in routes.webViewSupported
  useEffect(function () {
    if (isWebView && !route.webViewSupported) {
      webview.closeScreen()["catch"](function () {
        document.location.href = "app://close";
      });
    }
  }, [route.webViewSupported, isWebView, supportGooglePay, hideGTM]);
  // Handle auth redirection if needed
  var ResolvedElement = route.shouldAuth && !isLoggedIn ? function () {
    return /*#__PURE__*/React.createElement(Navigate, {
      to: createUrl(SIGN_UP_URL, {
        returnTo: getCurrentUrl(),
        referralLocation: route.login_event_tracking__IS_IT_NEEDED
      })
    });
  } : Element;

  // If no providers, just render the component
  if (!route.providers || route.providers.length === 0) {
    return /*#__PURE__*/React.createElement(ResolvedElement, null);
  }

  // Render the element with all its providers
  return route.providers.reduceRight(function (children, Provider) {
    return /*#__PURE__*/React.createElement(Suspense, {
      fallback: /*#__PURE__*/React.createElement("div", null, "Loading provider...")
    }, /*#__PURE__*/React.createElement(Provider, null, children));
  }, /*#__PURE__*/React.createElement(ResolvedElement, null));
};
var App = function App(_ref2) {
  var routes = _ref2.routes;
  useEffect(function () {
    /*
        @TODO: change AB_FINANCE_UX_ENHANCEMENT to FF_FINANCE_UX_ENHANCEMENT
        and update all related usage of it on the components
    */
    document.body.classList.add('AB_FINANCE_UX_ENHANCEMENT');
  }, []);
  useFirstRenderHistoryLogic();
  useMaintainSearchResultsDataStorage();
  useKeyboardDetector("keyboardOpen");
  useDisableClickWebview({
    className: "disableOnWebView"
  });
  useSentry();
  useSetReferral();
  useScrollToTop();
  var _useSession = useSession(),
    isLoggedIn = _useSession.isLoggedIn,
    user = _useSession.user;
  var _useAuthActions = useAuthActions(),
    isAuthInitialized = _useAuthActions.isAuthInitialized;
  var _useConsent = useConsent(),
    consentState = _useConsent.consentState;
  var lang = useLang();
  var location = useLocation();
  var isWebView = useIsWebView();
  var isIos = useIsIOS();
  useInitDataLayerConfig({
    isWebView: isWebView,
    isIos: isIos
  });
  useEffect(function () {
    if (!isLoggedIn) return;
    oldUserIdPropertyEvent(user.phone_hashed, "window_load");
  }, []);
  useHashToLocalStoreEvent();
  var showCustomizedPromoCode = checkFeatureFlag("FE_CUTOMIZED_PROMO_CODE");
  useEffect(function () {
    unbxdAnalytics({
      url: serverUrl + location.pathname + location.search,
      action: "visitor"
    });
  }, []);
  if (!isAuthInitialized()) {
    return /*#__PURE__*/React.createElement(Loader, null);
  }
  return routes ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ConsentModal, {
    isVisible: consentState === null || consentState === void 0 ? void 0 : consentState.showConsentModal
  }), showCustomizedPromoCode && /*#__PURE__*/React.createElement(GlobalPromoManager, {
    defaultDisplayMode: "auto",
    defaultPosition: "bottom"
  }), /*#__PURE__*/React.createElement(VideoRenderer, null), /*#__PURE__*/React.createElement(ConfirmPopup, null), SHOW_DEBUG_INFO && /*#__PURE__*/React.createElement(SyarahLabs, null), /*#__PURE__*/React.createElement(Toaster, {
    position: "bottom-center",
    toastOptions: {
      style: {
        // show on top of all elements
        zIndex: 99999,
        border: "1px solid #713200",
        padding: "16px",
        color: "#713200"
      }
    }
  }), /*#__PURE__*/React.createElement(ToastContainer, null), /*#__PURE__*/React.createElement(SonnerToast, null), /*#__PURE__*/React.createElement(TamaraProvider, {
    publicKey: TAMRA_WIDGET_PUBLIC_KEY,
    lang: lang,
    country: "SA",
    environment: TAMARA_WIDGET_ENVIRONMENT
  }, /*#__PURE__*/React.createElement(Routes, null, routes.map(function (route, index) {
    return /*#__PURE__*/React.createElement(Route, {
      key: index,
      path: route.path,
      exact: route.exact,
      element: /*#__PURE__*/React.createElement(RouteWithProviders, {
        route: route,
        isLoggedIn: isLoggedIn
      })
    });
  }), /*#__PURE__*/React.createElement(Route, {
    path: "*",
    element: /*#__PURE__*/React.createElement(Page404, null)
  })))) : null;
};
export default App;